import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { setValidationState } from "reducers/main";
import {
  useGetClaimValidationsQuery,
  useGetClaimQueryResponsesQuery,
  useGetClaimByIdQuery,
} from "services/claims-api";
import type { ClaimsDataType } from "types/DataTypes";
import { setClaimsFormData, setCurrentClaim } from "services/claimsDataSlice";
import { useDispatch } from "utils/redux";
import type { OptionType } from "components/inputs";
import {
  Select,
  TextInput,
  STRING_TO_DATE,
  DatePicker,
} from "components/inputs";
import type {
  QueryResponseFileLogDetailType,
  QRFLDPartCDType,
} from "types/DataTypes";
import Card from "components/Card/Card.tsx";
import ClaimsPageContainer from "./ClaimsPageContainer";

const PartCDItemComponent: React.FC<{
  item: QRFLDPartCDType;
  part_type: string;
  index: number;
}> = ({ item, part_type, index }) => {
  const maxCoverageDateItem = Array.from({ length: 15 }, (_, i) => 15 - i).find(
    (num) => {
      if (item[`dtp${num}_end`] || item[`dtp${num}_start`]) {
        return num;
      }
    },
  );

  const coverageItems = Array.from(
    { length: maxCoverageDateItem },
    (_, i) => i + 1,
  ).flatMap((num) => {
    return [
      <DatePicker
        name={`dtp${num}_start`}
        label={`Coverage Start ${num}`}
        initialDate={STRING_TO_DATE(item[`dtp${num}_start`], "yyyyMMdd")}
        readOnly={true}
        key={`dtp${num}_start`}
      />,
      <DatePicker
        name={`dtp${num}_end`}
        label={`Coverage End ${num}`}
        initialDate={STRING_TO_DATE(item[`dtp${num}_end`], "yyyyMMdd")}
        readOnly={true}
        key={`dtp${num}_end`}
      />,
    ];
  });

  return (
    <Card
      heading={`Part ${part_type} - ${item.entity_loop_items[0].name}`}
      key={`part_${part_type}_${index}`}
    >
      <TextInput
        label="Name"
        name="name"
        value={item.entity_loop_items[0].name}
        onChange={() => {}}
        disabled={true}
        readOnly={true}
      />
      <TextInput
        label="PBP"
        name="pbp_number"
        value={item.pbp_number}
        onChange={() => {}}
        disabled={true}
        readOnly={true}
      />
      <TextInput
        label="Contract Number"
        name="contract_number"
        value={item.benefit_contract_number}
        onChange={() => {}}
        disabled={true}
        readOnly={true}
      />
      <TextInput
        label="Address 1"
        name="address1"
        value={item.entity_loop_items[0].address1}
        onChange={() => {}}
        disabled={true}
        readOnly={true}
      />
      <TextInput
        label="Address 2"
        name="address2"
        value={item.entity_loop_items[0].address2}
        onChange={() => {}}
        disabled={true}
        readOnly={true}
      />
      <TextInput
        label="City"
        name="city"
        value={item.entity_loop_items[0].city}
        onChange={() => {}}
        disabled={true}
        readOnly={true}
      />
      <TextInput
        label="State"
        name="state"
        value={item.entity_loop_items[0].state}
        onChange={() => {}}
        disabled={true}
        readOnly={true}
      />
      <TextInput
        label="Zipcode"
        name="postal_code"
        value={item.entity_loop_items[0].postal_code}
        onChange={() => {}}
        disabled={true}
        readOnly={true}
      />
      {coverageItems}
    </Card>
  );
};

const ClaimQueryResponses = () => {
  const { claimId } = useParams<{ claimId: string }>();
  const dispatch = useDispatch();

  const [queryResponseId, setQueryResponseId] = useState<number | null>(null);
  const [queryResponse, setQueryResponse] =
    useState<QueryResponseFileLogDetailType | null>(null);

  const { data: claimValidation } = useGetClaimValidationsQuery(
    { id: claimId },
    { skip: !claimId },
  );

  const { data: claimQueryResponses } = useGetClaimQueryResponsesQuery(
    { id: claimId },
    { skip: false },
  );

  const { data: claim, isLoading: isClaimLoading } = useGetClaimByIdQuery<{
    data: ClaimsDataType;
    isLoading: boolean;
  }>({ id: claimId }, { skip: !claimId });

  useEffect(() => {
    if (!isClaimLoading) {
      dispatch(setClaimsFormData(claim));
      dispatch(setCurrentClaim(claim));
    }
  }, [dispatch, isClaimLoading]);

  useEffect(() => {
    if (claimValidation) {
      dispatch(
        setValidationState({
          errors: claimValidation.filter((e) => e.level === "ERROR"),
          warnings: claimValidation.filter((e) => e.level === "WARN"),
        }),
      );
    }
  }, [claimValidation, dispatch]);

  useEffect(() => {
    if (queryResponseId) {
      const selectedQrfld = (
        claimQueryResponses as QueryResponseFileLogDetailType[]
      )?.find((qrfld) => qrfld.id === queryResponseId);

      setQueryResponse(selectedQrfld);
    } else {
      setQueryResponse(null);
    }
  }, [claimQueryResponses, queryResponseId]);

  const claimQueryResponseOptions = (
    claimQueryResponses as QueryResponseFileLogDetailType[]
  )?.map((queryResponse: QueryResponseFileLogDetailType) => ({
    label: `Sent: ${queryResponse.send_date}, Disposition Code: ${queryResponse.disposition_code}`,
    value: queryResponse.id,
  }));

  return (
    <ClaimsPageContainer>
      <form className="overview">
        <Card heading="Query Response">
          <Select
            name={"query_response_id"}
            placeholder="Select Query Response"
            label={"Select Query Response To View"}
            options={claimQueryResponseOptions}
            onChange={(option: OptionType) => {
              setQueryResponseId(option?.value as number);
            }}
          />
        </Card>

        {queryResponse && (
          <>
            <Card heading="Overview" key="overview">
              <TextInput
                label="Date Sent"
                name="send_date"
                value={queryResponse.send_date}
                onChange={() => {}}
                disabled={true}
                readOnly={true}
              />
              <TextInput
                label="Response Date"
                name="response_date"
                value={queryResponse.response_date}
                onChange={() => {}}
                disabled={true}
                readOnly={true}
              />
              <TextInput
                label="Disposition Code"
                name="disposition_code"
                value={queryResponse.disposition_code}
                onChange={() => {}}
                disabled={true}
                readOnly={true}
              />
              <TextInput
                label="HICN"
                name="hicn"
                value={queryResponse.hicn}
                onChange={() => {}}
                disabled={true}
                readOnly={true}
              />
              <TextInput
                label="SSN"
                name="ssn"
                value={queryResponse.ssn}
                onChange={() => {}}
                disabled={true}
                readOnly={true}
              />
              <TextInput
                label="First Initial"
                name="first_initial"
                value={queryResponse.first_initial}
                onChange={() => {}}
                disabled={true}
                readOnly={true}
              />
              <TextInput
                label="Last Name"
                name="last_name"
                value={queryResponse.last_name}
                onChange={() => {}}
                disabled={true}
                readOnly={true}
              />
              <TextInput
                label="Birth Date"
                name="dob"
                value={queryResponse.dob}
                onChange={() => {}}
                disabled={true}
                readOnly={true}
              />
              <TextInput
                label="Sex Code"
                name="sex_code"
                value={queryResponse.sex_code}
                onChange={() => {}}
                disabled={true}
                readOnly={true}
              />
            </Card>
            <Card heading="Part A and B" key="part_ab">
              <DatePicker
                label="Part A Ent. Date"
                name="part_a_entitlement_date"
                initialDate={STRING_TO_DATE(
                  queryResponse.part_a_entitlement_date,
                )}
                readOnly={true}
                key="part_a_entitlement_date"
              />
              <DatePicker
                label="Part A Term. Date"
                name="part_a_termination_date"
                initialDate={STRING_TO_DATE(
                  queryResponse.part_a_termination_date,
                )}
                readOnly={true}
                key="part_a_termination_date"
              />

              <DatePicker
                label="Part B Ent. Date"
                name="part_b_entitlement_date"
                initialDate={STRING_TO_DATE(
                  queryResponse.part_b_entitlement_date,
                )}
                readOnly={true}
                key="part_b_entitlement_date"
              />
              <DatePicker
                label="Part B Term. Date"
                name="part_b_termination_date"
                initialDate={STRING_TO_DATE(
                  queryResponse.part_b_termination_date,
                )}
                readOnly={true}
                key="part_b_termination_date"
              />
            </Card>
            {queryResponse.part_c_items
              ? queryResponse.part_c_items.map((item, index) => (
                  <PartCDItemComponent
                    key={`part_c_${index}`}
                    part_type="C"
                    item={item}
                    index={index}
                  />
                ))
              : null}
            {queryResponse.part_d_items
              ? queryResponse.part_d_items.map((item, index) => (
                  <PartCDItemComponent
                    key={`part_d_${index}`}
                    part_type="D"
                    item={item}
                    index={index}
                  />
                ))
              : null}
          </>
        )}
      </form>
    </ClaimsPageContainer>
  );
};

export default ClaimQueryResponses;
